import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Avatar, Loading, Empty } from "../components/common";
import { Store, Search, MapPin, ExternalLink } from "lucide-react";

const TYPES = ["All", "Business", "Venue", "Label", "Agency", "Promoter", "TV Network", "Creator", "Other"];

export default function Businesses() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [items, setItems] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (type !== "All") p.set("type", type);
      api.get(`/businesses/explore?${p.toString()}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, type]);

  return (
    <div className="px-4 md:px-6 py-5" data-testid="businesses-page">
      <div className="flex items-center gap-2 mb-1"><Store size={22} className="text-gold" /><h1 className="font-display font-bold text-2xl">Business Directory</h1></div>
      <p className="text-sm text-muted-foreground mb-4">Discover venues, labels, agencies & shops across the virtual worlds.</p>

      <div className="flex items-center gap-2 surface rounded-full px-4 h-11 mb-3">
        <Search size={16} className="text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses" className="bg-transparent flex-1 outline-none text-sm" data-testid="businesses-search" />
      </div>
      <div className="flex gap-2 overflow-x-auto hide-scroll mb-5">
        {TYPES.map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`biz-type-${t.replace(/\s/g, "")}`}
            className={`shrink-0 px-3.5 h-9 rounded-full text-sm whitespace-nowrap transition-colors ${type === t ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {!items ? <Loading /> : items.length === 0 ? <Empty icon={Store} title="No businesses yet" hint="VirtuStars+ members can create a business from their profile." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b) => (
            <div key={b.id} className="surface rounded-2xl overflow-hidden card-hover" data-testid={`business-${b.id}`}>
              <div className="h-24 bg-black relative">
                {b.banner_url && <img src={mediaUrl(b.banner_url)} alt="" className="w-full h-full object-cover" />}
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-black/70 text-gold rounded px-1.5 py-0.5">{b.type}</span>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2.5 -mt-9 mb-2">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-surface-2 ring-4 ring-[#030303] shrink-0">
                    {b.logo_url ? <img src={mediaUrl(b.logo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Store size={18} /></div>}
                  </div>
                </div>
                <div className="font-display font-semibold truncate">{b.name}</div>
                {b.about && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{b.about}</p>}
                <button onClick={() => nav(`/${b.owner?.username}`)} className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground hover:text-eblue" data-testid={`business-owner-${b.id}`}>
                  <Avatar src={b.owner?.avatar_url} name={b.owner?.display_name} size={18} />by {b.owner?.display_name}
                </button>
                {b.in_world_url && <a href={b.in_world_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 mt-2 text-xs text-gold hover:underline" data-testid={`business-inworld-${b.id}`}><MapPin size={13} />Visit in-world <ExternalLink size={11} /></a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
